// Copyright © 2026 Gian Marco Cappelli — SAPIENS (finsapiens.it). Tutti i diritti riservati. Sviluppato su commissione esclusiva. Vietata la riproduzione senza autorizzazione scritta.
/**
 * CryptoImposte — Deployment 14
 *
 * Calcolatore client-side delle plusvalenze cripto secondo
 * L. 207/2024 art. 1 c. 24 (in vigore dal 1° gennaio 2026).
 *
 * - Soglia di esenzione: ABOLITA → qualsiasi plusvalenza è tassabile
 * - Aliquote: 33% crypto + USD-stablecoin non MiCAR / 26% stablecoin EUR MiCAR
 * - Costo base: LIFO (Circ. AdE n. 30/E/2023)
 * - 100% client-side: ZERO chiamate al backend per i calcoli
 */
import React, { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { Plus, Trash2, ShieldCheck, AlertTriangle } from 'lucide-react';
import { EducBox } from '../calculators/shared';

const ASSET_TYPES = [
  { id: 'crypto',         label: 'Crypto standard (BTC, ETH, altcoin)',                rate: 33 },
  { id: 'stable_eur',     label: 'Stablecoin EUR conformi MiCAR (EURC, EURT)',         rate: 26 },
  { id: 'stable_usd',     label: 'Stablecoin USD non MiCAR-compliant (USDT, USDC)',    rate: 33 },
];

const todayISO = () => new Date().toISOString().slice(0, 10);
const fmt = (n) => Number(n || 0).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const newRow = () => ({
  id: `row_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
  data_acquisto: todayISO(),
  prezzo_acquisto: '',
  data_vendita: todayISO(),
  prezzo_vendita: '',
  quantita: '',
  asset_type: 'crypto',
});

export default function CryptoImposte() {
  const [rows, setRows] = useState([newRow()]);

  const setRow = (id, key, val) => setRows(prev => prev.map(r => r.id === id ? { ...r, [key]: val } : r));
  const addRow = () => setRows(prev => [...prev, newRow()]);
  const removeRow = (id) => setRows(prev => {
    const next = prev.filter(r => r.id !== id);
    // Mantieni sempre almeno una riga visibile
    return next.length === 0 ? [newRow()] : next;
  });

  // ── Calcolo client-side ────────────────────────────────────────────────
  const result = useMemo(() => {
    let totalGain = 0;
    let totalLoss = 0;
    const perAsset = {}; // {asset_type: {gain, loss, tax}}

    rows.forEach(r => {
      const qty = Number(r.quantita) || 0;
      const buy = Number(r.prezzo_acquisto) || 0;
      const sell = Number(r.prezzo_vendita) || 0;
      if (qty <= 0 || buy <= 0 || sell <= 0) return;
      const cost = buy * qty;
      const proceeds = sell * qty;
      const pnl = proceeds - cost;
      const at = ASSET_TYPES.find(a => a.id === r.asset_type) || ASSET_TYPES[0];
      const bucket = perAsset[at.id] = perAsset[at.id] || { rate: at.rate, label: at.label, gain: 0, loss: 0 };
      if (pnl >= 0) { bucket.gain += pnl; totalGain += pnl; }
      else { bucket.loss += -pnl; totalLoss += -pnl; }
    });

    // Compensazione plusvalenze ↔ minusvalenze dell'anno corrente
    let netGainTaxable = 0;
    let unusedLoss = 0;
    Object.values(perAsset).forEach(b => {
      const net = b.gain - b.loss;
      if (net > 0) {
        b.taxable = net;
        b.tax = net * (b.rate / 100);
        netGainTaxable += net;
      } else {
        b.taxable = 0;
        b.tax = 0;
        unusedLoss += -net;
      }
    });
    const totalTax = Object.values(perAsset).reduce((s, b) => s + (b.tax || 0), 0);

    return { totalGain, totalLoss, netGainTaxable, totalTax, unusedLoss, perAsset };
  }, [rows]);

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} data-testid="crypto-imposte-calc" className="space-y-5">

      {/* GDPR-style privacy notice */}
      <div className="p-4 rounded-xl flex items-start gap-3"
        style={{ background: 'rgba(61,122,82,0.08)', border: '1px solid rgba(61,122,82,0.3)' }}
        data-testid="crypto-privacy-notice">
        <ShieldCheck className="w-5 h-5 text-[#3D7A52] flex-shrink-0 mt-0.5" />
        <div className="text-sm text-[#888780]">
          <strong className="text-[#3D7A52]">100% privato — calcolo nel tuo browser.</strong> Nessun dato delle tue transazioni cripto viene mai inviato al server.
        </div>
      </div>

      <EducBox text="Inserisci le tue operazioni crypto del 2026 per stimare l'imposta dovuta. Dal 1° gennaio 2026 (L. 207/2024 art. 1 c. 24) la soglia di esenzione di €2.000 è abolita: qualsiasi plusvalenza è tassabile." />

      {/* INPUT ROWS */}
      <div className="space-y-2.5" data-testid="crypto-rows">
        <div className="hidden lg:grid grid-cols-12 gap-2 text-xs text-[#888780] px-2">
          <div className="col-span-2">Data acquisto</div>
          <div className="col-span-2">Prezzo acquisto (€)</div>
          <div className="col-span-2">Data vendita</div>
          <div className="col-span-2">Prezzo vendita (€)</div>
          <div className="col-span-1">Quantità</div>
          <div className="col-span-2">Tipo asset</div>
          <div className="col-span-1"></div>
        </div>
        {rows.map((r, idx) => (
          <div key={r.id} className="grid grid-cols-2 lg:grid-cols-12 gap-2 p-3 rounded-xl"
            style={{ background: '#252523', border: '1px solid rgba(255,255,255,0.06)' }}
            data-testid={`crypto-row-${idx}`}>
            <input type="date" value={r.data_acquisto} onChange={e => setRow(r.id, 'data_acquisto', e.target.value)}
              className="col-span-1 lg:col-span-2 bg-[#1A1A18] border border-white/[0.08] rounded-md px-3 py-2 text-sm text-[#E8E8E4]"
              data-testid={`crypto-data-acquisto-${idx}`} />
            <input type="number" step="0.01" min="0" value={r.prezzo_acquisto} onChange={e => setRow(r.id, 'prezzo_acquisto', e.target.value)}
              placeholder="es. 25000"
              className="col-span-1 lg:col-span-2 bg-[#1A1A18] border border-white/[0.08] rounded-md px-3 py-2 text-sm text-[#E8E8E4] sapiens-no-spinner"
              data-testid={`crypto-prezzo-acquisto-${idx}`} />
            <input type="date" value={r.data_vendita} onChange={e => setRow(r.id, 'data_vendita', e.target.value)}
              className="col-span-1 lg:col-span-2 bg-[#1A1A18] border border-white/[0.08] rounded-md px-3 py-2 text-sm text-[#E8E8E4]"
              data-testid={`crypto-data-vendita-${idx}`} />
            <input type="number" step="0.01" min="0" value={r.prezzo_vendita} onChange={e => setRow(r.id, 'prezzo_vendita', e.target.value)}
              placeholder="es. 30000"
              className="col-span-1 lg:col-span-2 bg-[#1A1A18] border border-white/[0.08] rounded-md px-3 py-2 text-sm text-[#E8E8E4] sapiens-no-spinner"
              data-testid={`crypto-prezzo-vendita-${idx}`} />
            <input type="number" step="0.0001" min="0" value={r.quantita} onChange={e => setRow(r.id, 'quantita', e.target.value)}
              placeholder="qtà"
              className="col-span-1 lg:col-span-1 bg-[#1A1A18] border border-white/[0.08] rounded-md px-3 py-2 text-sm text-[#E8E8E4] sapiens-no-spinner"
              data-testid={`crypto-quantita-${idx}`} />
            <select value={r.asset_type} onChange={e => setRow(r.id, 'asset_type', e.target.value)}
              className="col-span-1 lg:col-span-2 bg-[#1A1A18] border border-white/[0.08] rounded-md px-3 py-2 text-sm text-[#E8E8E4]"
              data-testid={`crypto-asset-type-${idx}`}>
              {ASSET_TYPES.map(a => (
                <option key={a.id} value={a.id}>{a.label} — {a.rate}%</option>
              ))}
            </select>
            <button onClick={() => removeRow(r.id)}
              data-testid={`crypto-remove-row-${idx}`}
              aria-label="Rimuovi riga"
              className="col-span-2 lg:col-span-1 flex items-center justify-center rounded-md transition-colors"
              style={{ background: 'rgba(180,60,60,0.08)', border: '1px solid rgba(180,60,60,0.25)', color: '#F08080', minHeight: 38 }}>
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>

      <button onClick={addRow}
        data-testid="crypto-add-row"
        className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold transition-colors"
        style={{ background: '#252523', color: '#C9A84C', border: '1px solid rgba(201,168,76,0.3)' }}>
        <Plus className="w-4 h-4" /> Aggiungi operazione
      </button>

      {/* RESULTS */}
      <div className="grid sm:grid-cols-3 gap-3" data-testid="crypto-results">
        <div className="rounded-xl p-4" style={{ background: '#252523', border: '1px solid rgba(255,255,255,0.06)' }}>
          <p className="text-xs text-[#888780]">Plusvalenze totali</p>
          <p className="text-xl font-bold text-[#E8E8E4]" data-testid="crypto-total-gain">€{fmt(result.totalGain)}</p>
        </div>
        <div className="rounded-xl p-4" style={{ background: '#252523', border: '1px solid rgba(255,255,255,0.06)' }}>
          <p className="text-xs text-[#888780]">Minusvalenze totali</p>
          <p className="text-xl font-bold text-[#F08080]" data-testid="crypto-total-loss">€{fmt(result.totalLoss)}</p>
        </div>
        <div className="rounded-xl p-4" style={{ background: 'rgba(201,168,76,0.08)', border: '1px solid rgba(201,168,76,0.3)' }}>
          <p className="text-xs text-[#C9A84C] font-semibold">Imposta dovuta (2026)</p>
          <p className="text-2xl font-bold text-[#C9A84C]" data-testid="crypto-total-tax">€{fmt(result.totalTax)}</p>
        </div>
      </div>

      {/* Per-asset breakdown */}
      {Object.keys(result.perAsset).length > 0 && (
        <div className="rounded-xl overflow-hidden" style={{ border: '1px solid rgba(255,255,255,0.06)' }}>
          <div className="p-3 text-xs font-medium text-[#888780]" style={{ background: '#252523' }}>
            Dettaglio per tipo asset (compensazione plus/minus dell'anno corrente)
          </div>
          <table className="w-full text-sm">
            <tbody>
              {Object.values(result.perAsset).map((b, i) => (
                <tr key={i} className="border-t border-white/[0.04]">
                  <td className="py-2.5 px-3 text-[#888780]">{b.label}</td>
                  <td className="py-2.5 px-3 text-right text-[#E8E8E4]">+€{fmt(b.gain)}</td>
                  <td className="py-2.5 px-3 text-right text-[#F08080]">−€{fmt(b.loss)}</td>
                  <td className="py-2.5 px-3 text-right text-[#888780]">netto €{fmt(b.taxable)}</td>
                  <td className="py-2.5 px-3 text-right text-[#C9A84C] font-semibold">{b.rate}% = €{fmt(b.tax)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {result.unusedLoss > 0 && (
        <div className="p-4 rounded-xl text-sm" style={{ background: 'rgba(232,144,58,0.08)', border: '1px solid rgba(232,144,58,0.3)' }} data-testid="crypto-unused-loss">
          <p className="text-[#E8903A] font-semibold mb-1">Minusvalenze non compensate: €{fmt(result.unusedLoss)}</p>
          <p className="text-xs text-[#888780]">Riportabili nei 4 anni fiscali successivi (compensazione solo con future plusvalenze crypto).</p>
        </div>
      )}

      {/* Mandatory notes */}
      <div className="rounded-xl p-4 space-y-2.5 text-sm" style={{ background: '#252523', border: '1px solid rgba(255,255,255,0.06)' }} data-testid="crypto-notes">
        <p className="text-[#E8E8E4] font-semibold mb-1">📌 Note importanti</p>
        <ul className="list-disc list-inside text-[#888780] space-y-1.5">
          <li data-testid="crypto-note-rw">Il Quadro RW/W va compilato ogni anno anche in assenza di plusvalenze e anche se non hai venduto nulla — è sufficiente il possesso al 31 dicembre</li>
          <li data-testid="crypto-note-compensation">Le minusvalenze crypto compensano solo future plusvalenze crypto (non azioni o ETF) entro 4 anni fiscali</li>
          <li data-testid="crypto-note-dac8">Gli exchange registrati in UE comunicano automaticamente i dati all'Agenzia delle Entrate tramite la direttiva DAC8</li>
          <li data-testid="crypto-note-lifo">Il costo di acquisto viene calcolato con metodo LIFO (Circ. AdE n. 30/E/2023) — conserva tutta la documentazione delle transazioni</li>
        </ul>
      </div>

      {/* Deployment 26 — Sezione 4: vecchio crypto-fiscal-disclaimer rimosso,
          sostituito da <LegalDisclaimer showLegalUpdate /> più sotto (uniforme MiFID II). */}

      <p className="text-xs text-[#888780] italic" data-testid="crypto-doc-note">
        I calcoli sono basati sui dati inseriti — conserva tutta la documentazione delle transazioni per la dichiarazione ufficiale.
      </p>
</motion.div>
  );
}
